<template>
  <div class="home-container">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="bg-shape shape-1"></div>
      <div class="bg-shape shape-2"></div>
      <div class="bg-shape shape-3"></div>
    </div>

    <!-- Hero Section with Particles -->
    <section class="hero-section">
      <div class="hero-content">
        <div class="hero-badge animate-float">
          <svg
            class="w-4 h-4 text-red-500"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            />
          </svg>
          <span>Feito com amor em</span>
          <svg
            class="cape-verde-flag"
            viewBox="0 0 30 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- Fundo Azul -->
            <rect width="30" height="20" fill="#003893" />
            <!-- Listras Brancas -->
            <rect y="8" width="30" height="1.5" fill="#FFFFFF" />
            <rect y="11.5" width="30" height="1.5" fill="#FFFFFF" />
            <!-- Listra Vermelha -->
            <rect y="9.5" width="30" height="2" fill="#CF2027" />
            <!-- Estrelas (círculo de 10 estrelas) -->
            <g fill="#F7D116">
              <circle cx="8" cy="10" r="0.4" />
              <circle cx="9.2" cy="8.2" r="0.4" />
              <circle cx="11" cy="7.5" r="0.4" />
              <circle cx="12.8" cy="8.2" r="0.4" />
              <circle cx="14" cy="10" r="0.4" />
              <circle cx="12.8" cy="11.8" r="0.4" />
              <circle cx="11" cy="12.5" r="0.4" />
              <circle cx="9.2" cy="11.8" r="0.4" />
              <circle cx="8.5" cy="10.9" r="0.4" />
              <circle cx="8.5" cy="9.1" r="0.4" />
            </g>
          </svg>
        </div>

        <h1 class="hero-title">
          Bem-vindo ao
          <span class="hero-highlight">
            <span class="hero-text-gradient">BlogDavid</span>
            <svg
              class="hero-underline"
              viewBox="0 0 200 12"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 7 C 50 3, 150 3, 198 7"
                stroke="url(#gradient)"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop
                    offset="0%"
                    style="stop-color: #6366f1; stop-opacity: 1"
                  />
                  <stop
                    offset="100%"
                    style="stop-color: #8b5cf6; stop-opacity: 1"
                  />
                </linearGradient>
              </defs>
            </svg>
          </span>
        </h1>

        <p class="hero-description">
          Descubra histórias fascinantes, compartilhe suas ideias e conecte-se
          com uma comunidade apaixonada por conhecimento
        </p>

        <!-- Enhanced Search -->
        <div class="search-container">
          <div class="search-wrapper">
            <div class="search-icon-wrapper">
              <SearchIcon class="search-icon" />
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar posts por título, tema ou conteúdo..."
              class="search-input"
              data-tutorial="search"
            />

            <!-- Loading indicator -->
            <div v-if="loading && searchQuery" class="search-loader">
              <div class="spinner-search"></div>
            </div>

            <!-- Clear button -->
            <button
              v-if="searchQuery && !loading"
              @click="searchQuery = ''"
              class="search-clear"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>

            <div class="search-glow"></div>
          </div>

          <!-- Search Stats -->
          <div v-if="searchQuery && !loading" class="search-stats">
            <span class="search-stats-text">
              {{ posts.length }}
              {{
                posts.length === 1
                  ? "resultado encontrado"
                  : "resultados encontrados"
              }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Posts Feed -->
    <section class="posts-section">
      <div class="section-header">
        <div class="section-title-wrapper">
          <div class="section-icon">
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              ></path>
            </svg>
          </div>
          <h2 class="section-title">
            {{
              searchQuery
                ? `Resultados para "${searchQuery}"`
                : "Posts em Destaque"
            }}
          </h2>
        </div>

        <!-- Create Post Button (Admin only) -->
        <router-link
          v-if="authStore.isAdmin"
          to="/admin/create-post"
          class="create-post-btn"
        >
          <div class="create-btn-icon">
            <PlusIcon class="w-5 h-5" />
          </div>
          <span>Criar Post</span>
          <div class="create-btn-shine"></div>
        </router-link>
      </div>

      <!-- Loading Skeleton -->
      <div v-if="loading && posts.length === 0" class="posts-grid">
        <div v-for="i in 6" :key="i" class="skeleton-card">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-line skeleton-line-small"></div>
            <div class="skeleton-line skeleton-line-large"></div>
            <div class="skeleton-line skeleton-line-medium"></div>
          </div>
        </div>
      </div>

      <!-- Posts Grid -->
      <div v-else-if="posts.length > 0" class="posts-grid">
        <article
          v-for="(post, index) in posts"
          :key="post.id"
          class="post-card"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <!-- Post Number Badge -->
          <div class="post-number">
            <span>{{ String(index + 1).padStart(2, "0") }}</span>
          </div>

          <!-- Post Image -->
          <div class="post-image-wrapper" @click="goToPost(post.id)">
            <div class="post-image-container">
              <img
                v-if="post.image_url"
                :src="post.image_url"
                :alt="post.title"
                class="post-image"
              />
              <div v-else class="post-image-placeholder">
                <FileTextIcon class="w-16 h-16 text-white/80" />
              </div>

              <!-- Image Overlay -->
              <div class="post-image-overlay">
                <div class="overlay-icon">
                  <svg
                    class="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Post Content -->
          <div class="post-content">
            <!-- Author & Date -->
            <div class="post-meta">
              <UserAvatar :user="post.author" size="md" />
              <div class="post-meta-info">
                <span class="author-name">{{ post.author.username }}</span>
                <div class="post-date">
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <span>{{ formatDate(post.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Title -->
            <h3 class="post-title" @click="goToPost(post.id)">
              {{ post.title }}
            </h3>

            <!-- Content Preview -->
            <p class="post-excerpt">
              {{ createContentPreview(post.content, 120) }}
            </p>

            <!-- Actions Bar -->
            <div class="post-actions">
              <!-- Like Button -->
              <button
                @click.stop="toggleLike(post)"
                :disabled="!authStore.isAuthenticated"
                class="action-button"
                :class="{ 'action-active': post.userInteractions?.liked }"
                :data-tutorial="index === 0 ? 'like' : undefined"
              >
                <div class="action-icon-wrapper">
                  <HeartIcon
                    class="action-icon"
                    :class="{
                      'action-icon-filled': post.userInteractions?.liked,
                    }"
                  />
                </div>
                <span class="action-count">{{ post.stats.likes }}</span>
              </button>

              <!-- Comment Button -->
              <button
                @click.stop="goToPost(post.id)"
                class="action-button"
                :data-tutorial="index === 0 ? 'comment' : undefined"
              >
                <div class="action-icon-wrapper">
                  <MessageCircleIcon class="action-icon" />
                </div>
                <span class="action-count">{{ post.stats.comments }}</span>
              </button>

              <!-- Read More Button -->
              <button @click="goToPost(post.id)" class="read-more-btn">
                <span>Ler artigo</span>
                <svg
                  class="read-more-arrow"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </article>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <FileTextIcon class="w-20 h-20" />
        </div>
        <h3 class="empty-title">
          {{ searchQuery ? "Nenhum post encontrado" : "Nenhum post ainda" }}
        </h3>
        <p class="empty-description">
          {{
            searchQuery
              ? "Tente buscar por outro tema."
              : "Seja o primeiro a criar um post!"
          }}
        </p>
        <router-link
          v-if="authStore.isAdmin"
          to="/admin/create-post"
          class="empty-action"
        >
          <PlusIcon class="w-5 h-5" />
          <span>Criar Primeiro Post</span>
        </router-link>
      </div>

      <!-- Load More -->
      <div
        v-if="hasMore && !loading && posts.length > 0"
        class="load-more-container"
      >
        <button @click="loadMore" class="load-more-btn" :disabled="loadingMore">
          <span v-if="loadingMore" class="flex items-center space-x-2">
            <div class="spinner-small"></div>
            <span>Carregando mais posts...</span>
          </span>
          <span v-else class="flex items-center space-x-2">
            <span>Carregar Mais Posts</span>
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { useTutorialStore } from "@/store/tutorial";
import apiService from "@/services/api";
import { createContentPreview } from "@/utils/contentUtils";
import UserAvatar from "@/components/UserAvatar.vue";
import {
  SearchIcon,
  PlusIcon,
  FileTextIcon,
  HeartIcon,
  MessageCircleIcon,
} from "lucide-vue-next";

export default {
  name: "Home",
  components: {
    SearchIcon,
    PlusIcon,
    FileTextIcon,
    HeartIcon,
    MessageCircleIcon,
    UserAvatar,
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const tutorialStore = useTutorialStore();

    const posts = ref([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const searchQuery = ref("");
    const currentPage = ref(1);
    const hasMore = ref(true);
    const searchTimeout = ref(null);

    const fetchPosts = async (page = 1, append = false) => {
      try {
        if (append) {
          loadingMore.value = true;
        } else {
          loading.value = true;
          posts.value = [];
          currentPage.value = 1;
        }

        const params = {
          module: "posts",
          action: "list",
          page,
          limit: 12,
        };

        if (searchQuery.value.trim()) {
          params.search = searchQuery.value.trim();
        }

        console.log("📤 Fazendo requisição fetchPosts:", {
          url: "/api/handlers",
          params,
        });

        // ⚡ Com retry automático para cold starts
        const response = await apiService.get("/api/handlers", {
          params,
          retries: 3,
          retryDelay: 1500,
        });

        console.log("📥 Resposta recebida:", response);
        console.log("📊 Estrutura da resposta:", {
          temData: !!response.data,
          temDataData: !!response.data?.data,
          temPosts: !!response.data?.data?.posts,
          tipoPosts: Array.isArray(response.data?.data?.posts),
        });

        // 🛡️ Validação defensiva
        let postsArray = [];
        if (response.data?.data?.posts) {
          postsArray = response.data.data.posts;
        } else if (response.data?.posts) {
          postsArray = response.data.posts;
        } else if (Array.isArray(response.data)) {
          postsArray = response.data;
        } else if (Array.isArray(response)) {
          postsArray = response;
        } else {
          console.error("❌ Formato de resposta não reconhecido:", response);
          throw new Error("Formato de resposta inválido");
        }

        if (append) {
          posts.value.push(...postsArray);
        } else {
          posts.value = postsArray;
        }

        hasMore.value = postsArray.length === 12;
        currentPage.value = page;

        console.log("✅ Posts carregados:", posts.value.length);
      } catch (error) {
        console.error("Erro ao carregar posts:", error);

        // Mostrar mensagem amigável ao usuário
        if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
          console.warn(
            "⚠️ Servidor demorando para responder (cold start). Tente novamente."
          );
        } else if (error.response?.status === 500) {
          console.error(
            "❌ Erro no servidor. Por favor, tente novamente em alguns segundos."
          );
        }

        // Se não conseguiu carregar e não estava fazendo append, manter posts vazios
        if (!append) {
          posts.value = [];
        }
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const handleSearch = () => {
      fetchPosts(1, false);
    };

    const loadMore = () => {
      if (hasMore.value && !loadingMore.value) {
        fetchPosts(currentPage.value + 1, true);
      }
    };

    const goToPost = (postId) => {
      router.push(`/posts/${postId}`);
    };

    const toggleLike = async (post) => {
      if (!authStore.isAuthenticated) {
        router.push("/login");
        return;
      }

      // Optimistic UI update
      const wasLiked = post.userInteractions?.liked;
      const previousLikes = post.stats.likes;

      if (!post.userInteractions) {
        post.userInteractions = {};
      }

      post.userInteractions.liked = !wasLiked;
      post.stats.likes = wasLiked ? previousLikes - 1 : previousLikes + 1;

      try {
        await apiService.post("/api/handlers", null, {
          params: {
            module: "posts",
            action: "like",
            id: post.id,
          },
        });
      } catch (error) {
        console.error("Erro ao dar like:", error);
        // Revert on error
        post.userInteractions.liked = wasLiked;
        post.stats.likes = previousLikes;
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    // Watch search query for real-time search
    watch(searchQuery, (newValue) => {
      // Clear previous timeout
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      // If search is empty, fetch all posts immediately
      if (!newValue.trim()) {
        fetchPosts(1, false);
        return;
      }

      // Debounce search for 500ms
      searchTimeout.value = setTimeout(() => {
        fetchPosts(1, false);
      }, 500);
    });

    onMounted(() => {
      fetchPosts();
    });

    return {
      authStore,
      tutorialStore,
      posts,
      loading,
      loadingMore,
      searchQuery,
      hasMore,
      handleSearch,
      loadMore,
      goToPost,
      toggleLike,
      formatDate,
      createContentPreview,
    };
  },
};
</script>

<style scoped>
/* ========================================
   LAYOUT & CONTAINER
   ======================================== */
.home-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

/* ========================================
   ANIMATED BACKGROUND
   ======================================== */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%);
  overflow: hidden;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.shape-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  top: 50%;
  right: -150px;
  animation-delay: 5s;
}

.shape-3 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  bottom: -150px;
  left: 50%;
  animation-delay: 10s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
}

/* ========================================
   HERO SECTION
   ======================================== */
.hero-section {
  position: relative;
  padding: 6rem 1rem 4rem;
  text-align: center;
  z-index: 1;
}

.hero-content {
  max-width: 900px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6366f1;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  animation: fadeInDown 0.8s ease-out;
}

.cape-verde-flag {
  width: 24px;
  height: 16px;
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
}

.hero-badge:hover .cape-verde-flag {
  transform: scale(1.1) rotate(5deg);
}

@keyframes animate-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: animate-float 3s ease-in-out infinite;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 800;
  color: #1a202c;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.hero-highlight {
  position: relative;
  display: inline-block;
  margin-left: 0.5rem;
}

.hero-text-gradient {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0%,
  100% {
    filter: hue-rotate(0deg);
  }
  50% {
    filter: hue-rotate(20deg);
  }
}

.hero-underline {
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 12px;
  animation: drawLine 1.5s ease-out 0.5s backwards;
}

@keyframes drawLine {
  from {
    stroke-dasharray: 200;
    stroke-dashoffset: 200;
  }
  to {
    stroke-dasharray: 200;
    stroke-dashoffset: 0;
  }
}

.hero-description {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #64748b;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto 3rem;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}

/* ========================================
   SEARCH CONTAINER
   ======================================== */
.search-container {
  max-width: 600px;
  margin: 0 auto;
  animation: fadeInUp 0.8s ease-out 0.6s backwards;
}

.search-wrapper {
  position: relative;
  background: white;
  border-radius: 20px;
  padding: 0.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.search-wrapper:focus-within {
  box-shadow: 0 20px 60px rgba(99, 102, 241, 0.2);
  transform: translateY(-2px);
}

.search-icon-wrapper {
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
}

.search-icon {
  width: 20px;
  height: 20px;
  color: #9ca3af;
  transition: color 0.3s ease;
}

.search-wrapper:focus-within .search-icon {
  color: #6366f1;
}

.search-input {
  width: 100%;
  padding: 1rem 3.5rem;
  font-size: 1rem;
  color: #1f2937;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 16px;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  border-color: #6366f1;
}

.search-loader {
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.spinner-search {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.search-clear {
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.5rem;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-clear:hover {
  background: #e5e7eb;
  transform: translateY(-50%) scale(1.1);
}

.search-glow {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%) scaleX(0);
  width: 80%;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
  border-radius: 2px;
  transition: transform 0.3s ease;
}

.search-wrapper:focus-within .search-glow {
  transform: translateX(-50%) scaleX(1);
}

.search-stats {
  margin-top: 1rem;
  text-align: center;
  animation: fadeIn 0.3s ease;
}

.search-stats-text {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 999px;
}

/* ========================================
   POSTS SECTION
   ======================================== */
.posts-section {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 1rem 5rem;
  z-index: 1;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  }
}

.section-title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: #1a202c;
}

.create-post-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-weight: 600;
  font-size: 0.9375rem;
  border-radius: 12px;
  text-decoration: none;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
}

.create-post-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(99, 102, 241, 0.4);
}

.create-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  transition: transform 0.3s ease;
}

.create-post-btn:hover .create-btn-icon {
  transform: rotate(90deg);
}

.create-btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.create-post-btn:hover .create-btn-shine {
  left: 100%;
}

/* ========================================
   POSTS GRID
   ======================================== */
.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2rem;
}

@media (max-width: 768px) {
  .posts-grid {
    grid-template-columns: 1fr;
  }
}

/* ========================================
   POST CARD
   ======================================== */
.post-card {
  position: relative;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.6s ease-out backwards;
}

.post-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

.post-number {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 700;
  color: #6366f1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.post-card:hover .post-number {
  transform: scale(1.1) rotate(5deg);
}

.post-image-wrapper {
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.post-image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.post-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.post-card:hover .post-image {
  transform: scale(1.1) rotate(2deg);
}

.post-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.post-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.post-card:hover .post-image-overlay {
  opacity: 1;
}

.overlay-icon {
  transform: scale(0.8);
  transition: transform 0.3s ease;
}

.post-card:hover .overlay-icon {
  transform: scale(1);
}

.post-content {
  padding: 1.75rem;
}

.post-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.author-avatar {
  flex-shrink: 0;
}

.avatar-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f3f4f6;
}

.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  border: 2px solid #f3f4f6;
}

.post-meta-info {
  flex: 1;
  min-width: 0;
}

.author-name {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.post-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #9ca3af;
}

.post-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.4;
  margin-bottom: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-title:hover {
  color: #6366f1;
}

.post-excerpt {
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f3f4f6;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-2px);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-button.action-active {
  background: #fef2f2;
  border-color: #fecaca;
  color: #ef4444;
}

.action-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
}

.action-button:hover .action-icon {
  transform: scale(1.1);
}

.action-icon-filled {
  fill: #ef4444;
  color: #ef4444;
  animation: heartBeat 0.5s ease;
}

@keyframes heartBeat {
  0%,
  100% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.3);
  }
  50% {
    transform: scale(1.1);
  }
  75% {
    transform: scale(1.2);
  }
}

.action-count {
  font-weight: 600;
}

.read-more-btn {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.read-more-btn:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.read-more-arrow {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.read-more-btn:hover .read-more-arrow {
  transform: translateX(4px);
}

/* ========================================
   SKELETON LOADING
   ======================================== */
.skeleton-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 16/9;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 1.75rem;
}

.skeleton-line {
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 0.75rem;
}

.skeleton-line-small {
  width: 40%;
}

.skeleton-line-large {
  width: 90%;
}

.skeleton-line-medium {
  width: 70%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ========================================
   EMPTY STATE
   ======================================== */
.empty-state {
  text-align: center;
  padding: 5rem 1rem;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  margin-bottom: 2rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1.125rem;
  color: #64748b;
  margin-bottom: 2rem;
}

.empty-action {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
}

.empty-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(99, 102, 241, 0.4);
}

/* ========================================
   LOAD MORE
   ======================================== */
.load-more-container {
  margin-top: 3rem;
  text-align: center;
}

.load-more-btn {
  padding: 1rem 2rem;
  background: white;
  color: #6366f1;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.load-more-btn:hover:not(:disabled) {
  background: #6366f1;
  color: white;
  border-color: #6366f1;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========================================
   ANIMATIONS
   ======================================== */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
